<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Brand logo-->
      <b-link class="brand-logo">
        <b-img
          fluid
          src="@/assets/images/logo/Udara360-logo.png"
          width="180"
        />
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col
        lg="8"
        class="d-none d-lg-flex p-0"
      >
        <div
          v-if="institution.loginPageBackgroundURL"
          class="auth-bg-image"
          role="img"
          aria-label="Institution background"
          :style="{
            backgroundImage: 'url(' + institution.loginPageBackgroundURL + ')',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            width: '100%',
            height: '100%',
          }"
        />
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-3"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-0 mx-auto"
        >
          <Alert
            ref="alertRef"
            :show="showFormError"
            :message="errorMessage"
          />

          <b-card-title class="mb-1">
            <strong>Set up your authenticator app</strong><br>
            <small>A virtual MFA is an application running on your device that can be configured by scanning QR code.</small>
            <hr>
          </b-card-title>

          <b-card-text class="mb-2">
            <b-row class="match-height">
              <b-col md="2">
                <b-badge
                  class="dot"
                  variant="primary"
                  pill
                >
                  <p class="mt-0">
                    1
                  </p>
                </b-badge>
              </b-col>
              <b-col
                md="10"
                class="text-justify"
              >
                Install compactible application such as Google Authenticator, Microsoft Authenticator, or Authy app on your mobile device or computer.
              </b-col>
            </b-row>
            <b-row class="match-height mt-2">
              <b-col md="2">
                <b-badge
                  class="dot"
                  variant="primary"
                  pill
                >
                  <p class="mt-0">
                    2
                  </p>
                </b-badge>
              </b-col>
              <b-col
                md="5"
                class="text-justify"
              >
                <qrcode-vue
                  :value="userOTPCredentials.binding.uri"
                  :size="size"
                  level="H"
                />
              </b-col>
              <b-col
                md="5"
                class="text-justify"
              >
                Open your authenticator app, and use the app to scan the QR code.<br> Alternatively, you can type the secret key.<br>
                <LinkText
                  class="secondary mb-2"
                  :label="isSecret ? 'Hide secret key' : 'Show secret key'"
                  :on-click="showSecret"
                />
              </b-col>
            </b-row>
            <b-row
              v-if="isSecret"
              class="match-height mt-0"
            >
              <b-col md="1" />
              <b-col md="11">
                <InputField
                  id="secret-key"
                  v-model="userOTPCredentials.binding.secret"
                  label="Secret Key"
                  placeholder="Secret Key"
                  type="text"
                />
              </b-col>
            </b-row>
            <b-row class="match-height">
              <b-col
                md="2"
                class="mt-3"
              >
                <b-badge
                  class="dot"
                  variant="primary"
                  pill
                >
                  <p class="mt-0">
                    3
                  </p>
                </b-badge>
              </b-col>
              <b-col
                md="10"
                class="text-justify mt-3"
              >
                Fill in code from your MFA device
                <validation-observer ref="activateFormRule">
                  <b-form
                    class="auth-login-form"
                    @submit.prevent="activateOTPAction"
                  >
                    <!-- Verification code -->
                    <InputField
                      id="code"
                      v-model="code"
                      label="MFA Code"
                      placeholder="Enter code"
                      type="text"
                      rules="required"
                      error-name="Code"
                    />
                    <!-- submit button -->
                    <Button
                      label="Activate"
                      :loading="isLoading"
                      :disabled="isDisabled"
                      size="lg"
                      block
                      @click="activateOTPAction"
                    />
                  </b-form>
                </validation-observer>
              </b-col>
            </b-row>
          </b-card-text>
          <!-- form -->
          <b-card-text class="text-center mt-2">
            <b-link :to="{name:'auth-login'}">
              <feather-icon icon="ChevronLeftIcon" /> Back to login
            </b-link>
          </b-card-text>
        </b-col>
      </b-col>
    <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BLink, BCardText, BCardTitle, BImg, BForm, BBadge,
} from 'bootstrap-vue'
import { required } from '@validations'
import { mapActions, mapGetters } from 'vuex'
import { toastAlert } from '@core/mixins/toast'
// eslint-disable-next-line import/no-cycle
import QrcodeVue from 'qrcode.vue'
import InputField from '../../forms/InputField.vue'
import Button from '../../components/button/Button.vue'
import Alert from '../../components/alert/Alert.vue'
import LinkText from '../../components/LinkText.vue'

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    ValidationObserver,
    InputField,
    Button,
    QrcodeVue,
    BBadge,
    Alert,
    LinkText,
  },
  mixins: [toastAlert],
  data() {
    return {
      isSecret: false,
      isVisible: false,
      userDetails: {
        userToken: '',
      },
      userOTPCredentials: {},
      code: '',
      size: 130,
      email: '',
      password: '',
      hasResentToken: false,
      isResendingToken: false,
      isLoading: false,
      isDisabled: false,
      showFormError: false,
      errorMessage: '',
      status: '',
      timer: '',
      required,
    }
  },
  computed: {
    ...mapGetters({
      institution: 'auth/getInstitution',
    }),
  },
  watch: {
    hasResentToken(newValue) {
      if (newValue) {
        // eslint-disable-next-line no-return-assign
        this.timer = setTimeout(() => (this.hasResentToken = false), 1000 * 3)
      }
      if (!newValue) {
        clearTimeout(this.timer)
      }
    },
  },
  mounted() {
    this.getUser()
    this.fetchUserOTPToken()
  },
  methods: {
    ...mapActions({
      otpActivationAction: 'auth/otpActivationAction',
      getUserOTPCredentials: 'auth/getUserOTPCredentials',
    }),
    getUser() {
      const { user } = this.$route.params
      if (user.username) {
        this.email = user.username
        this.password = user.password
      } else {
        this.$router.push({ name: 'auth-login' })
      }
    },
    showSecret() {
      this.isSecret = !this.isSecret
    },
    hideSecret() {
      this.isSecret = false
    },
    async fetchUserOTPToken() {
      try {
        const response = await this.getUserOTPCredentials({ email: this.email, password: this.password })
        const { status, data } = response
        if (status === 200 && data) {
          this.userOTPCredentials = data
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async activateOTPAction() {
      this.$refs.activateFormRule.validate().then(async success => {
        if (success) {
          try {
            this.showFormError = false
            this.isLoading = true
            this.isDisabled = true
            const formData = {
              code: this.code,
              email: this.email,
            }
            const response = await this.otpActivationAction(formData)
            const { status, data } = response
            if (status === 200 && data) {
              this.$router
                .push({ name: 'auth-login' })
              this.toastAlert({
                title: 'Success',
                icon: 'CoffeeIcon',
                variant: 'success',
                text: 'Your authenticator app activation was successful.',
              })
              this.showFormError = true
            }
          } catch (error) {
            error.response.data.errors.forEach(responseError => {
              this.errorMessage = responseError.errorMessage
              this.showFormError = true
            })
          } finally {
            this.isLoading = false
            this.isDisabled = false
          }
        }
        return false
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
.text-justify {
    text-align: justify;
    font-size: 11.5px;
}
.dot {
  height: 30px;
  width: 30px;
  border-radius: 50%;
  display: inline-block;
}
</style>
